import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  DEFAULT_PAGINATION_MODEL,
  getSensorLabel,
  normalizeSelectionModel,
  toHistoryKey,
  getLatestHistoryEntry,
} from './useSensorDataTab.utils';
import { buildSensorDataColumns } from './useSensorDataTab.columns';
import {
  loadCapteurs,
  getFallbackCapteurs,
  fetchSensorDataBundle,
  restoreSensorRow,
  deleteSensorRowsBulk,
  saveSensorEditsBulk,
} from './useSensorDataTab.data';

export const useSensorDataTab = () => {
  const projectsList = useSelector(
    (state) => state.authentifie?.filtre_projects_list_id ?? []
  );
  const reduxCapteurs = useSelector(
    (state) => state.capteur?.capteursListe ?? []
  );
  const currentUserId = useSelector((state) => state.authentifie?.user_id);

  const [capteurs, setCapteurs] = useState([]);
  const [loadingCapteurs, setLoadingCapteurs] = useState(true);
  // { [sensorId]: number[] } – sensorId → selected positions
  const [sensorPositionMap, setSensorPositionMap] = useState({});
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [nValues, setNValues] = useState('');
  const [rows, setRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState(
    DEFAULT_PAGINATION_MODEL
  );
  const [pendingEdits, setPendingEdits] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);
  const [historyMap, setHistoryMap] = useState({});
  const [historyPopupData, setHistoryPopupData] = useState(null);

  useEffect(() => {
    if (!projectsList?.length) {
      setCapteurs([]);
      setSensorPositionMap({});
      setLoadingCapteurs(false);
      return;
    }

    setLoadingCapteurs(true);
    setError(null);
    loadCapteurs(projectsList)
      .then(({ capteurs: nextCapteurs, initialSensorPositionMap }) => {
        setCapteurs(nextCapteurs);
        setSensorPositionMap(initialSensorPositionMap);
      })
      .catch(() => {
        const fallback = getFallbackCapteurs();
        setCapteurs(fallback.capteurs);
        setSensorPositionMap(fallback.initialSensorPositionMap);
        setError('Impossible de charger la liste des capteurs.');
      })
      .finally(() => setLoadingCapteurs(false));
  }, [projectsList]);

  const fetchData = useCallback(async () => {
    setLoadingData(true);
    setError(null);
    setFetched(false);
    setPendingEdits({});
    setHistoryMap({});

    try {
      const {
        rows: fetchedRows,
        historyMap: nextHistoryMap,
        hasActiveFilters,
      } = await fetchSensorDataBundle({
        sensorPositionMap,
        startDateTime,
        endDateTime,
        nValues,
        userId: currentUserId,
      });

      setPaginationModel({ page: 0, pageSize: hasActiveFilters ? -1 : 25 });
      setRows(fetchedRows);
      setHistoryMap(nextHistoryMap);
      setRowSelectionModel([]);
      setFetched(true);
    } catch (fetchError) {
      setError(
        fetchError?.message || 'Impossible de charger les données du capteur.'
      );
    } finally {
      setLoadingData(false);
    }
  }, [currentUserId, sensorPositionMap, endDateTime, nValues, startDateTime]);

  const handleProcessRowUpdate = useCallback((newRow, oldRow) => {
    const baseOldTimeUnix = oldRow.old_time_unix ?? oldRow.time_unix;
    const baseOldValue = oldRow.old_value ?? oldRow.value;
    const baseOldCalcValue = oldRow.old_calc_value ?? oldRow.calc_value;

    const hasTimeUnixChanged = newRow.time_unix !== baseOldTimeUnix;
    const hasValueChanged = newRow.value !== baseOldValue;
    const hasCalcValueChanged = newRow.calc_value !== baseOldCalcValue;

    if (!hasTimeUnixChanged && !hasValueChanged && !hasCalcValueChanged) {
      const reverted = {
        ...newRow,
        _dirty: false,
        old_time_unix: baseOldTimeUnix,
        old_value: baseOldValue,
        old_calc_value: baseOldCalcValue,
      };
      setRows((prev) => prev.map((r) => (r.id === reverted.id ? reverted : r)));
      setPendingEdits((prev) => {
        const next = { ...prev };
        delete next[reverted.id];
        return next;
      });
      return reverted;
    }

    const updated = {
      ...newRow,
      _dirty: true,
      old_time_unix: baseOldTimeUnix,
      old_value: baseOldValue,
      old_calc_value: baseOldCalcValue,
    };
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setPendingEdits((prev) => ({ ...prev, [updated.id]: updated }));
    return updated;
  }, []);

  const handleProcessRowUpdateError = useCallback((err) => {
    console.error('[SensorDataTab] processRowUpdate error:', err);
    setError('Erreur lors de la modification de la ligne.');
  }, []);

  const handleRowSelectionModelChange = useCallback(
    (nextSelectionModel) => {
      const normalized = normalizeSelectionModel(nextSelectionModel);

      // Fallback: if header "select all" returns all rows, keep only current page rows.
      if (
        paginationModel.pageSize !== -1 &&
        rows.length > 0 &&
        normalized.length === rows.length
      ) {
        const start = paginationModel.page * paginationModel.pageSize;
        const end = start + paginationModel.pageSize;
        setRowSelectionModel(rows.slice(start, end).map((row) => row.id));
        return;
      }

      setRowSelectionModel(normalized);
    },
    [paginationModel.page, paginationModel.pageSize, rows]
  );

  const getNumericSensorId = useCallback((row) => {
    const rawSensorId = row?._sensorId ?? row?.sensor_id ?? row?.sensorId;
    const sensorId = Number(rawSensorId);
    return Number.isFinite(sensorId) ? sensorId : null;
  }, []);

  const handleOpenHistory = useCallback((row, entries) => {
    setHistoryPopupData({ row, entries });
  }, []);

  const handleCloseHistory = useCallback(() => {
    setHistoryPopupData(null);
  }, []);

  const handleRestore = useCallback(
    async (id) => {
      const row = rows.find((r) => r.id === id);
      if (!row) return;
      const key = toHistoryKey(row._sensorId, row.pk_test);
      const currentEntries = historyMap[key] ?? [];
      const latestEntry = getLatestHistoryEntry(currentEntries);
      if (latestEntry?.update_type !== 'Delete') {
        return;
      }

      setError(null);

      try {
        const restoreResult = await restoreSensorRow({
          row,
          userId: currentUserId,
        });
        if (!restoreResult?.skipped) {
          await fetchData();
        }
      } catch (restoreError) {
        console.error('[SensorDataTab] handleRestore API error:', restoreError);
        setError('Erreur lors de la restauration de la ligne.');
      }
    },
    [currentUserId, fetchData, historyMap, rows]
  );

  const toggleSensor = useCallback((sensorId) => {
    // Only update the selection; keep the current view/data until the user
    // reloads with "Charger les données".
    setSensorPositionMap((prev) => {
      if (prev[sensorId]) {
        const next = { ...prev };
        delete next[sensorId];
        return next;
      }
      return { ...prev, [sensorId]: [1] };
    });
  }, []);

  const togglePosition = useCallback((sensorId, position) => {
    setSensorPositionMap((prev) => {
      const current = prev[sensorId] ?? [];
      const next = current.includes(position)
        ? current.filter((p) => p !== position)
        : [...current, position].sort((a, b) => a - b);
      return { ...prev, [sensorId]: next };
    });
  }, []);

  const deleteRows = useCallback(
    async (targetRows) => {
      const { deletedIds, failedCount } = await deleteSensorRowsBulk({
        targetRows,
        getNumericSensorId,
        userId: currentUserId,
      });

      if (deletedIds.length > 0) {
        const deletedIdsSet = new Set(deletedIds);
        setRows((prev) => prev.filter((row) => !deletedIdsSet.has(row.id)));
        setPendingEdits((prev) => {
          const next = { ...prev };
          deletedIdsSet.forEach((id) => delete next[id]);
          return next;
        });
        setRowSelectionModel((prev) =>
          prev.filter((selectedId) => !deletedIdsSet.has(selectedId))
        );
      }

      return { deletedIds, failedCount };
    },
    [currentUserId, getNumericSensorId]
  );

  const handleDelete = useCallback(
    async (id) => {
      const row = rows.find((r) => r.id === id);
      const shouldDelete = window.confirm(
        `Supprimer cette donnée ? Cette action est irréversible.\nDonnées supprimées : pk_test = ${row?.pk_test ?? id} valeur = ${row?.value ?? 'N/A'}`
      );
      if (!shouldDelete) return;

      try {
        const { deletedIds, failedCount } = await deleteRows(row ? [row] : []);
        if (deletedIds.length > 0) {
          await fetchData();
        }
        if (!deletedIds.length || failedCount > 0) {
          setError('Erreur lors de la suppression de la ligne.');
        }
      } catch {
        setError('Erreur lors de la suppression de la ligne.');
      }
    },
    [deleteRows, fetchData, rows]
  );

  const handleBulkDelete = useCallback(async () => {
    if (!rowSelectionModel.length) return;

    const selectedRows = rows.filter((row) =>
      rowSelectionModel.includes(row.id)
    );
    const shouldDelete = window.confirm(
      `Supprimer ${selectedRows.length} donnée(s) ? Cette action est irréversible.`
    );
    if (!shouldDelete) return;

    setError(null);

    try {
      const { deletedIds, failedCount } = await deleteRows(selectedRows);
      if (deletedIds.length > 0) {
        await fetchData();
      }
      if (failedCount > 0) {
        setError(
          `Erreur lors de la suppression en lot (${failedCount} ligne(s) en échec).`
        );
      }
    } catch {
      setError('Erreur lors de la suppression en lot.');
    }
  }, [deleteRows, fetchData, rowSelectionModel, rows]);

  const handleSaveAll = useCallback(async () => {
    const edits = Object.values(pendingEdits);
    if (!edits.length) return;
    setSaving(true);
    setError(null);

    try {
      const { savedIds, failedCount } = await saveSensorEditsBulk({
        edits,
        getNumericSensorId,
        userId: currentUserId,
      });

      if (savedIds.length > 0) {
        await fetchData();
      }

      if (failedCount > 0) {
        setError(
          `Erreur lors de la sauvegarde (${failedCount} ligne(s) en échec).`
        );
      }
    } catch {
      setError(
        'Erreur lors de la sauvegarde. Vérifiez votre connexion et réessayez.'
      );
    } finally {
      setSaving(false);
    }
  }, [currentUserId, fetchData, getNumericSensorId, pendingEdits]);

  const sensorNameById = useMemo(() => {
    const map = {};
    const allCapteurs = [
      ...(Array.isArray(reduxCapteurs) ? reduxCapteurs : []),
      ...(Array.isArray(capteurs) ? capteurs : []),
    ];

    allCapteurs.forEach((capteur) => {
      const sensorId = Number(capteur?.sensor_id ?? capteur?.id);
      if (Number.isFinite(sensorId)) {
        map[sensorId] = getSensorLabel(capteur, sensorId);
      }
    });
    return map;
  }, [capteurs, reduxCapteurs]);

  // sensorId_position → "prefix (position)" label, mirroring chip display
  const positionLabelMap = useMemo(() => {
    const map = {};
    const allCapteurs = [
      ...(Array.isArray(reduxCapteurs) ? reduxCapteurs : []),
      ...(Array.isArray(capteurs) ? capteurs : []),
    ];

    allCapteurs.forEach((capteur) => {
      const sensorId = Number(capteur?.sensor_id ?? capteur?.id);
      if (!Number.isFinite(sensorId)) return;
      const components = Array.isArray(capteur?.component_list)
        ? capteur.component_list
        : [];
      components.forEach((component) => {
        const pos = Number(
          component?.position_in_sensor ?? component?.position
        );
        if (!Number.isFinite(pos) || pos <= 0) return;
        const prefix = String(component?.prefix ?? '').trim();
        map[`${sensorId}_${pos}`] = prefix
          ? `${prefix} (${pos})`
          : `Pos. (${pos})`;
      });
    });
    return map;
  }, [capteurs, reduxCapteurs]);

  const getPositionLabel = useCallback(
    (sensorId, position) => {
      const key = `${sensorId}_${position}`;
      return positionLabelMap[key] ?? `Pos. (${position})`;
    },
    [positionLabelMap]
  );

  const getSensorNameById = useCallback(
    (sensorId) => {
      if (!Number.isFinite(sensorId)) return '—';
      return sensorNameById[sensorId] ?? `Capteur ${sensorId}`;
    },
    [sensorNameById]
  );

  const columns = useMemo(
    () =>
      buildSensorDataColumns({
        handleDelete,
        getSensorNameById,
        getPositionLabel,
        historyMap,
        onOpenHistory: handleOpenHistory,
        handleRestore,
      }),
    [
      getSensorNameById,
      getPositionLabel,
      handleDelete,
      historyMap,
      handleOpenHistory,
      handleRestore,
    ]
  );

  const dirtyCount = Object.keys(pendingEdits).length;

  const hasSelection = Object.values(sensorPositionMap).some(
    (positions) => positions.length > 0
  );

  const selectionSummary = Object.entries(sensorPositionMap)
    .filter(([, positions]) => positions.length > 0)
    .map(([sId, positions]) => {
      const name = getSensorNameById(Number(sId));
      return `${name} (pos. ${positions.join(', ')})`;
    })
    .join(' · ');

  return {
    capteurs,
    loadingCapteurs,
    sensorPositionMap,
    toggleSensor,
    togglePosition,
    hasSelection,
    selectionSummary,
    startDateTime,
    setStartDateTime,
    endDateTime,
    setEndDateTime,
    nValues,
    setNValues,
    rows,
    rowSelectionModel,
    setRowSelectionModel,
    handleRowSelectionModelChange,
    loadingData,
    saving,
    error,
    fetched,
    paginationModel,
    setPaginationModel,
    columns,
    dirtyCount,
    pendingEdits,
    historyMap,
    fetchData,
    handleBulkDelete,
    handleSaveAll,
    handleProcessRowUpdate,
    handleProcessRowUpdateError,
    historyPopupData,
    handleCloseHistory,
    getSensorNameById,
  };
};
